import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "../atoms/button";
import { filterEventsUntil } from "./flowReplayTrace";
import { getSandboxTrace } from "./functions";

const POLL_INTERVAL_MS = 3000;
const POLL_BACKOFF_MS = 15000;
const POLL_UNAVAILABLE_MS = 30000;
const MAX_EVENTS = 200;
const MAX_TURNS = 6;
const STALL_AFTER_MS = 30000;

/**
 * Strings com escapes Unicode — mesma convenção do botOpsSandboxChat.
 * A aba do chat de teste já teve acentos corrompidos em produção; manter os
 * textos visíveis em ASCII puro torna o problema impossível de reaparecer.
 */
const TXT = {
    title: "Trilha do turno",
    subtitle: "Mais recente no topo \u00b7 clique no card para detalhes",
    refresh: "Atualizar",
    checking: "Verificando\u2026",
    lastCheck: "\u00daltima verifica\u00e7\u00e3o",
    owner: "Dono da conversa",
    ownerUnknown: "ainda n\u00e3o informado",
    chatwootNow: "Chatwoot agora",
    status: "Status",
    assigned: "Designa\u00e7\u00e3o",
    assignedYes: "Designada",
    assignedNo: "Sem designa\u00e7\u00e3o",
    team: "Equipe",
    inbox: "Inbox",
    handoffReason: "Motivo handoff",
    botGlobal: "Bot global",
    botOn: "ligado",
    botOff: "desligado",
    configVersion: "Config",
    turnsTitle: "Turnos",
    latestBadge: "mais recente",
    showSteps: "ver etapas",
    hideSteps: "ocultar etapas",
    expandHint: "Clique para ver etapas",
    collapseHint: "Clique para recolher",
    total: "total",
    code: "c\u00f3digo",
    stepCount: "etapas",
    emptyTitle: "Nada aconteceu ainda",
    checkingBody: "Lendo a trilha desta conversa\u2026",
    emptyBody:
        "Nenhuma etapa registrada para esta conversa. Envie uma mensagem no chat ao lado para o bot entrar em a\u00e7\u00e3o.",
    noConversationTitle: "Sem conversa selecionada",
    noConversationBody:
        "Inicie a sess\u00e3o sandbox no chat ao lado para a trilha come\u00e7ar a gravar.",
    unavailableTitle: "Trilha temporariamente indispon\u00edvel",
    unavailableBody:
        "N\u00e3o foi poss\u00edvel ler a trilha agora (API ou n8n). O painel tenta de novo sozinho \u2014 o chat ao lado continua funcionando normalmente.",
    errorTitle: "N\u00e3o foi poss\u00edvel ler a trilha",
    errorBody:
        "O chat ao lado continua funcionando. O painel tenta de novo em alguns segundos.",
    toastError:
        "Falha ao ler a trilha do turno. O painel tenta de novo em alguns segundos.",
    platformsTitle: "Por plataforma",
    execution: "execu\u00e7\u00e3o",
    decisionTitle: "Decis\u00e3o",
    decisionIntent: "Intent",
    decisionUrgency: "Urg\u00eancia",
    decisionHandoff: "Handoff",
    decisionEscalation: "Escala\u00e7\u00e3o",
    decisionTeam: "Equipe destino",
    decisionClientSummary: "Resumo do cliente",
    decisionTriage: "Racioc\u00ednio triagem",
    decisionHuman: "Escala\u00e7\u00e3o humana",
    decisionMgmt: "Escala\u00e7\u00e3o gest\u00e3o",
    decisionMotivo: "Motivo",
    decisionMotivoTexto: "Motivo (texto)",
    decisionSticky: "Intent sticky",
    decisionStickyMotivo: "Motivo sticky",
    decisionCrm: "CRM match",
    decisionCrmOpen: "Grading aberto",
    decisionYes: "sim",
    decisionNo: "n\u00e3o",
    memoryTitle: "O que o bot sabia",
    memoryJanela: "Janela (turnos)",
    memoryAmostra: "Hist\u00f3rico no prompt",
    memoryAmostraVazia: "Sem hist\u00f3rico ainda neste turno",
    memoryEstado: "Estado (mf_*)",
    memoryEstadoIntent: "Intent injetado",
    memoryEstadoUrgency: "Urg\u00eancia injetada",
    memoryEstadoResumo: "Resumo injetado",
    memoryEstadoVazio: "Nenhum estado estruturado injetado",
    memoryCrm: "CRM match",
    memoryCrmOpen: "Grading aberto",
    memoryCrmVazio: "CRM sem match ou indispon\u00edvel",
    // Diagnóstico do turno
    dxErro: "O turno falhou",
    dxErroDetail:
        "O fluxo registrou erro nesta etapa e parou. O bot n\u00e3o respondeu por causa disso.",
    dxIgnorado: "O bot n\u00e3o respondeu de prop\u00f3sito",
    dxHandoff: "Transferido para atendimento humano",
    dxHandoffDetail:
        "O bot passou a conversa para uma pessoa. Enquanto o dono for humano, ele n\u00e3o volta a responder.",
    dxHandoffPublicado: "Bot respondeu e transferiu para atendimento humano",
    dxPublicado: "Bot respondeu",
    dxPublicadoDetail:
        "A resposta foi publicada no Chatwoot e deve aparecer no chat ao lado.",
    dxSubstituido: "Rajada substitu\u00edda por mensagem mais nova",
    dxSubstituidoDetail:
        "O cliente escreveu de novo antes do bot fechar a resposta. Este turno foi descartado e a resposta sai no turno seguinte \u2014 n\u00e3o \u00e9 erro.",
    dxAndamento: "Gerando resposta",
    dxAndamentoDetail: "O turno est\u00e1 em andamento na etapa",
    dxParado: "Parado sem terminar",
    dxParadoDetail:
        "O turno travou nesta etapa e n\u00e3o avan\u00e7ou. Se continuar assim, vale olhar o n8n ou avisar ops.",
    // Motivos de "skipped" fora da tabela conhecida
    skipUnmapped:
        "O fluxo ignorou este turno, mas o motivo informado ainda n\u00e3o tem tradu\u00e7\u00e3o neste painel. Use o c\u00f3digo t\u00e9cnico abaixo e avise ops.",
    skipNoReason:
        "O fluxo ignorou este turno sem informar o motivo. Isso normalmente indica falha no workflow \u2014 avise ops.",
};

const PLATFORM_LABELS = {
    chatwoot: "Chatwoot",
    n8n: "n8n",
    ia: "IA / conhecimento",
};

/** Estágio → rótulo em pt-BR + plataforma onde a etapa acontece. */
export const STAGE_META = {
    "webhook.received": {
        label: "Mensagem chegou no fluxo",
        short: "Recebida",
        platform: "chatwoot",
    },
    "owner.resolved": {
        label: "Dono da conversa identificado",
        short: "Dono",
        platform: "chatwoot",
    },
    "debounce.appended": {
        label: "Mensagem entrou na rajada",
        short: "Rajada",
        platform: "n8n",
    },
    "debounce.flushed": {
        label: "Rajada fechada e enviada ao bot",
        short: "Rajada fechada",
        platform: "n8n",
    },
    "debounce.superseded": {
        label: "Rajada substitu\u00edda por mensagem mais nova",
        short: "Substitu\u00edda",
        platform: "n8n",
    },
    retrieval: {
        label: "Busca na base de conhecimento",
        short: "Conhecimento",
        platform: "ia",
    },
    "llm.request": {
        label: "Pergunta enviada ao modelo",
        short: "Modelo (envio)",
        platform: "ia",
    },
    "llm.response": {
        label: "Modelo devolveu a resposta",
        short: "Modelo (resposta)",
        platform: "ia",
    },
    "guardrail.checked": {
        label: "Guardrails verificados",
        short: "Guardrails",
        platform: "ia",
    },
    "intent.resolved": {
        label: "Intenção e destino do turno decididos",
        short: "Intent",
        platform: "ia",
    },
    "message.published": {
        label: "Resposta publicada no Chatwoot",
        short: "Publicada",
        platform: "chatwoot",
    },
    "handoff.triggered": {
        label: "Transferido para atendimento humano",
        short: "Handoff",
        platform: "chatwoot",
    },
    "owner.updated": {
        label: "Dono da conversa atualizado",
        short: "Dono atualizado",
        platform: "chatwoot",
    },
    skipped: { label: "Turno ignorado", short: "Ignorado", platform: "n8n" },
    error: { label: "Falha no turno", short: "Erro", platform: "n8n" },
};

/** Estágios que encerram o turno — o que não está aqui significa "ainda rodando". */
const TERMINAL_STAGES = new Set([
    "message.published",
    "handoff.triggered",
    "debounce.superseded",
    "skipped",
    "error",
]);

/**
 * Motivos de "skipped" traduzidos para português claro.
 *
 * Os códigos que o Ingress emite hoje estão em `scripts/n8n-lib/` do repositório
 * `mgs` — nas colas de `patch-ingress-debounce-redis.py` e
 * `patch-ingress-idempotencia.py`. Ao acrescentar um motivo novo lá, acrescente
 * aqui também: código não mapeado NUNCA aparece sozinho, cai no texto de
 * fallback com o código cru como detalhe técnico, mas fallback não explica nada
 * a quem está diagnosticando.
 *
 * Os demais motivos da tabela são de etapas que ainda não existem (turno de IA
 * nativo, guardrails próprios) e ficam prontos para quando existirem.
 */
export const SKIP_REASONS = {
    sem_redis_url:
        "O n\u00f3 de debounce n\u00e3o achou a URL do Redis (MANAFIX_DEBOUNCE_REDIS_URL). \u00c9 falha de configura\u00e7\u00e3o do n8n, n\u00e3o da conversa \u2014 avise ops.",
    sem_marcador:
        "O turno perdeu o marcador do debounce entre os n\u00f3s. Nenhuma execu\u00e7\u00e3o assumiu a resposta; avise ops.",
    rescheduled:
        "O sil\u00eancio de espera ainda n\u00e3o completou quando esta execu\u00e7\u00e3o acordou. Outra execu\u00e7\u00e3o responde por este turno.",
    juncao_vazia:
        "As partes agrupadas somaram texto vazio depois da limpeza. Nada para o bot interpretar.",
    buffer_vazio:
        "O buffer da rajada j\u00e1 havia sido consumido por outra execu\u00e7\u00e3o. Ignorar \u00e9 o comportamento correto.",
    nao_informado:
        "O fluxo encerrou o turno sem informar o motivo. Isso \u00e9 defeito do workflow, n\u00e3o da conversa \u2014 avise ops.",
    kill_switch:
        "O bot est\u00e1 desligado (kill switch). As mensagens v\u00e3o para a fila de Triagem e ningu\u00e9m responde automaticamente.",
    human_owner:
        "A conversa est\u00e1 com um atendente humano. O bot n\u00e3o responde enquanto o dono n\u00e3o voltar a ser ele.",
    conversation_resolved:
        "A conversa est\u00e1 marcada como resolvida no Chatwoot. Reabra a conversa para o bot voltar a responder.",
    outgoing_message:
        "O evento era uma mensagem de sa\u00edda (do pr\u00f3prio bot ou de um atendente), n\u00e3o uma pergunta do cliente.",
    private_note:
        "A mensagem era uma nota privada do Chatwoot, que o cliente n\u00e3o v\u00ea. O bot ignora notas.",
    empty_content: "A mensagem chegou sem texto para o bot interpretar.",
    unsupported_type:
        "A mensagem era s\u00f3 m\u00eddia (\u00e1udio, imagem ou arquivo) e o bot hoje s\u00f3 trata texto.",
    duplicate:
        "Evento repetido: esta mensagem j\u00e1 havia sido processada. Ignorar \u00e9 o comportamento correto.",
    superseded:
        "O cliente enviou outra mensagem antes do bot responder. Esta rajada foi descartada e a resposta sai no turno seguinte.",
    not_sandbox:
        "O evento veio de outra caixa de entrada, fora do sandbox. N\u00e3o pertence a este teste.",
    guardrail_blocked:
        "Os guardrails bloquearam a resposta gerada. O bot preferiu n\u00e3o responder a dizer algo proibido.",
    no_config:
        "O bot n\u00e3o encontrou a configura\u00e7\u00e3o ativa (prompt, guardrails e knowledge). Use Reaplicar no card Funcionamento.",
    rate_limit:
        "O turno foi descartado por limite de chamadas. Espere alguns segundos e tente de novo.",
    off_hours: "Fora do hor\u00e1rio de atendimento configurado para o bot.",
    no_agent_bot:
        "A conversa n\u00e3o est\u00e1 com o agent bot do Chatwoot, ent\u00e3o o fluxo autom\u00e1tico nem come\u00e7ou.",
    dry_run: "O fluxo rodou em modo de teste (dry run) e n\u00e3o publicou resposta.",
};

/** Variações de nome que o workflow pode mandar para o mesmo motivo. */
const SKIP_ALIASES = {
    // Emitidos hoje pelo Ingress, conferidos contra as colas dos patches.
    conteudo_vazio: "empty_content",
    empty: "buffer_vazio",
    duplicado: "duplicate",
    kill_switch_off: "kill_switch",
    kill_off: "kill_switch",
    kill_enabled_false: "kill_switch",
    bot_disabled: "kill_switch",
    bot_desligado: "kill_switch",
    owner_human: "human_owner",
    agent_owner: "human_owner",
    assigned_to_agent: "human_owner",
    handoff_active: "human_owner",
    atendimento_humano: "human_owner",
    dono_humano: "human_owner",
    resolved: "conversation_resolved",
    status_resolved: "conversation_resolved",
    conversa_resolvida: "conversation_resolved",
    message_from_bot: "outgoing_message",
    not_incoming: "outgoing_message",
    self_message: "outgoing_message",
    echo: "outgoing_message",
    mensagem_de_saida: "outgoing_message",
    nota_privada: "private_note",
    no_text: "empty_content",
    sem_texto: "empty_content",
    blank_message: "empty_content",
    unsupported_message_type: "unsupported_type",
    attachment_only: "unsupported_type",
    audio_message: "unsupported_type",
    midia_nao_suportada: "unsupported_type",
    duplicate_event: "duplicate",
    already_processed: "duplicate",
    evento_duplicado: "duplicate",
    debounce_superseded: "superseded",
    newer_message: "superseded",
    rajada_substituida: "superseded",
    other_inbox: "not_sandbox",
    wrong_inbox: "not_sandbox",
    inbox_ignorada: "not_sandbox",
    blocked_by_guardrail: "guardrail_blocked",
    guardrail: "guardrail_blocked",
    guardrail_block: "guardrail_blocked",
    missing_config: "no_config",
    config_missing: "no_config",
    sem_configuracao: "no_config",
    throttled: "rate_limit",
    limite_de_taxa: "rate_limit",
    outside_business_hours: "off_hours",
    fora_do_horario: "off_hours",
    bot_not_assigned: "no_agent_bot",
    sem_agent_bot: "no_agent_bot",
    test_mode: "dry_run",
};

function firstFilled(values) {
    for (const value of values) {
        if (value === 0) return "0";
        if (value == null) continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return null;
}

/** Normaliza o código do motivo: minúsculas, sem acento, separadores viram "_". */
export function normalizeReasonKey(raw) {
    if (raw == null) return null;
    let text = String(raw).trim().toLowerCase();
    if (!text) return null;
    try {
        text = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    } catch {
        // Ambiente sem normalize: segue com o texto original
    }
    text = text.replace(/[\s\-.:/\\]+/g, "_").replace(/[^a-z0-9_]/g, "");
    return text || null;
}

function tsValue(ts) {
    if (!ts) return 0;
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? 0 : parsed;
}

export function formatDuration(ms) {
    if (ms == null || Number.isNaN(ms)) return "\u2014";
    if (ms < 0) return "\u2014";
    if (ms < 1000) return `${Math.round(ms)} ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.round((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
}

function formatClock(ts) {
    const value = tsValue(ts);
    if (!value) return "\u2014";
    try {
        return new Date(value).toLocaleTimeString("pt-BR");
    } catch {
        return String(ts);
    }
}

export function stageLabel(stage) {
    return STAGE_META[stage]?.label ?? stage ?? "\u2014";
}

/**
 * Traduz um evento "skipped" para o motivo em português.
 * Devolve sempre { reason, code, mapped } — nunca só o código cru.
 */
export function describeSkip(event) {
    const attrs = event?.attrs ?? {};
    const raw = firstFilled([
        attrs.motivo,
        attrs.reason,
        attrs.skip_reason,
        attrs.code,
        attrs.rule,
        event?.reason,
    ]);
    const detail = firstFilled([
        attrs.detalhe,
        attrs.detail,
        attrs.mensagem,
        attrs.message,
    ]);
    const key = normalizeReasonKey(raw);
    const canonical = key ? (SKIP_ALIASES[key] ?? key) : null;
    const mapped = canonical ? SKIP_REASONS[canonical] : null;

    if (mapped) {
        return { reason: mapped, code: raw, mapped: true, detail };
    }
    if (raw) {
        return { reason: TXT.skipUnmapped, code: raw, mapped: false, detail };
    }
    return { reason: TXT.skipNoReason, code: null, mapped: false, detail };
}

/**
 * Quem é o dono da conversa agora.
 *
 * Chatwoot é a fonte da verdade (SoR): se `fallbackOwnerMode` veio do live,
 * ele manda — a trilha atrasa (owner.resolved no início do turno + handoff
 * sem `owner.updated`). Sem SoR, a trilha conta: `owner.*` e também
 * `handoff.triggered` (implica humano).
 */
export function describeOwner(events, fallbackOwnerMode = null) {
    const list = Array.isArray(events) ? events.filter(Boolean) : [];
    const ownerEvents = [];
    list.forEach((e) => {
        if (e.stage === "owner.resolved" || e.stage === "owner.updated") {
            ownerEvents.push(e);
            return;
        }
        if (e.stage === "handoff.triggered") {
            ownerEvents.push({
                ...e,
                attrs: { ...(e.attrs || {}), owner: "human" },
            });
        }
    });
    ownerEvents.sort((a, b) => tsValue(a.ts) - tsValue(b.ts));
    const last = ownerEvents[ownerEvents.length - 1];

    const nameFrom = (ev) => {
        const attrs = ev?.attrs ?? {};
        return firstFilled([
            attrs.owner_name,
            attrs.nome,
            attrs.atendente,
            attrs.assignee,
            attrs.agent,
        ]);
    };

    const fromMode = (mode, name = null) => {
        const key = normalizeReasonKey(mode);
        if (key === "bot" || key === "agent_bot" || key === "typebot" || key === "robot") {
            return { label: "Bot", kind: "bot", name };
        }
        if (
            key === "human" ||
            key === "humano" ||
            key === "agent" ||
            key === "atendente" ||
            key === "user"
        ) {
            return { label: "Humano", kind: "humano", name };
        }
        if (key === "none" || key === "unassigned" || key === "sem_dono" || key === "null") {
            return { label: "Sem dono definido", kind: "sem_dono", name };
        }
        return null;
    };

    // SoR (session/messages ou state do trace) — sempre que o live trouxe modo.
    const fb = String(fallbackOwnerMode || "")
        .trim()
        .toLowerCase();
    if (fb) {
        const fromSor = fromMode(fb, nameFrom(last));
        if (fromSor) return fromSor;
    }

    if (!last) {
        return { label: TXT.ownerUnknown, kind: "desconhecido", name: null };
    }

    const attrs = last.attrs ?? {};
    const raw = firstFilled([
        attrs.dono,
        attrs.owner,
        attrs.owner_type,
        attrs.assignee_type,
        attrs.tipo,
    ]);
    const name = nameFrom(last);
    const fromTrail = fromMode(raw, name);
    if (fromTrail) return fromTrail;
    return { label: raw ?? TXT.ownerUnknown, kind: "desconhecido", name };
}

/**
 * Agrupa eventos por turn_id: uma rajada de quatro mensagens vira UM turno.
 * Turnos saem do mais novo para o mais antigo; etapas em ordem cronológica
 * com a duração desde a etapa anterior.
 */
export function groupByTurn(events) {
    const list = Array.isArray(events) ? events.filter(Boolean) : [];
    const byTurn = new Map();

    list.forEach((event, index) => {
        const key = event.turn_id || `${event.conversation_id ?? "?"}:sem-turno`;
        if (!byTurn.has(key)) byTurn.set(key, []);
        byTurn.get(key).push({ event, index });
    });

    const turns = [];
    for (const [turnId, entries] of byTurn) {
        const sorted = [...entries].sort((a, b) => {
            const diff = tsValue(a.event.ts) - tsValue(b.event.ts);
            return diff !== 0 ? diff : a.index - b.index;
        });

        let previousMs = null;
        const steps = sorted.map(({ event }) => {
            const currentMs = tsValue(event.ts);
            const deltaMs =
                previousMs != null && currentMs ? Math.max(0, currentMs - previousMs) : null;
            if (currentMs) previousMs = currentMs;
            return { ...event, deltaMs };
        });

        const first = steps[0];
        const last = steps[steps.length - 1];
        const startedMs = tsValue(first?.ts);
        const endedMs = tsValue(last?.ts);

        turns.push({
            turnId,
            steps,
            startedAt: first?.ts ?? null,
            endedAt: last?.ts ?? null,
            startedMs,
            endedMs,
            totalMs: startedMs && endedMs ? Math.max(0, endedMs - startedMs) : null,
            executionId: firstFilled(steps.map((s) => s.execution_id)),
            currentStage: last?.stage ?? null,
            done: TERMINAL_STAGES.has(last?.stage),
        });
    }

    return turns.sort((a, b) => b.startedMs - a.startedMs);
}

/**
 * Responde "por que o bot não respondeu?" para um turno.
 * Precedência: erro > ignorado > handoff > publicado > substituído > em curso.
 */
export function buildTurnDiagnosis(turn, now = Date.now()) {
    const steps = turn?.steps ?? [];
    if (steps.length === 0) {
        return { tone: "vazio", headline: TXT.emptyTitle, detail: TXT.emptyBody, code: null };
    }

    const find = (stage) => steps.find((s) => s.stage === stage);
    const errorStep = find("error") ?? steps.find((s) => s.status === "erro");
    if (errorStep) {
        const attrs = errorStep.attrs ?? {};
        const detail = firstFilled([
            attrs.mensagem,
            attrs.message,
            attrs.detalhe,
            attrs.detail,
            attrs.erro,
            attrs.error,
        ]);
        return {
            tone: "erro",
            headline: TXT.dxErro,
            detail: detail ?? TXT.dxErroDetail,
            code: firstFilled([attrs.code, attrs.codigo, errorStep.stage]),
            atStage: errorStep.stage,
        };
    }

    const skipStep = find("skipped") ?? steps.find((s) => s.status === "ignorado");
    if (skipStep) {
        const skip = describeSkip(skipStep);
        return {
            tone: "ignorado",
            headline: TXT.dxIgnorado,
            detail: skip.reason,
            extra: skip.detail,
            code: skip.code,
            mapped: skip.mapped,
            atStage: skipStep.stage,
        };
    }

    const handoffStep = find("handoff.triggered");
    const publishedStep = find("message.published");
    if (handoffStep) {
        return {
            tone: "humano",
            headline: publishedStep ? TXT.dxHandoffPublicado : TXT.dxHandoff,
            detail: TXT.dxHandoffDetail,
            code: null,
        };
    }
    if (publishedStep) {
        return {
            tone: "ok",
            headline: TXT.dxPublicado,
            detail: TXT.dxPublicadoDetail,
            code: null,
        };
    }
    if (find("debounce.superseded")) {
        return {
            tone: "substituido",
            headline: TXT.dxSubstituido,
            detail: TXT.dxSubstituidoDetail,
            code: null,
        };
    }

    const last = steps[steps.length - 1];
    const idleMs = tsValue(last.ts) ? Math.max(0, now - tsValue(last.ts)) : 0;
    if (idleMs > STALL_AFTER_MS) {
        return {
            tone: "parado",
            headline: `${TXT.dxParado} \u2014 ${stageLabel(last.stage)} h\u00e1 ${formatDuration(idleMs)}`,
            detail: TXT.dxParadoDetail,
            code: null,
            atStage: last.stage,
        };
    }
    return {
        tone: "andamento",
        headline: TXT.dxAndamento,
        detail: `${TXT.dxAndamentoDetail} "${stageLabel(last.stage)}".`,
        code: null,
        atStage: last.stage,
    };
}

/** Resumo do que cada plataforma já fez no turno. */
export function summarizePlatforms(turn) {
    const steps = turn?.steps ?? [];
    const order = ["chatwoot", "n8n", "ia"];
    const buckets = new Map(order.map((id) => [id, []]));
    for (const step of steps) {
        const platform = STAGE_META[step.stage]?.platform;
        if (platform && buckets.has(platform)) buckets.get(platform).push(step);
    }
    return order
        .map((id) => {
            const list = buckets.get(id);
            const last = list[list.length - 1];
            return {
                id,
                label: PLATFORM_LABELS[id],
                count: list.length,
                lastShort: last ? (STAGE_META[last.stage]?.short ?? last.stage) : null,
            };
        })
        .filter((entry) => entry.count > 0);
}

function parseTriBool(value) {
    if (value === true || value === false) return value;
    const text = String(value ?? "")
        .trim()
        .toLowerCase();
    if (!text) return null;
    if (text === "true" || text === "1" || text === "yes" || text === "sim") return true;
    if (text === "false" || text === "0" || text === "no" || text === "nao" || text === "não") {
        return false;
    }
    return null;
}

function formatTriBool(value) {
    if (value === true) return TXT.decisionYes;
    if (value === false) return TXT.decisionNo;
    return null;
}

/**
 * Extrai a decisão do turno a partir do último intent.resolved / handoff.triggered,
 * com fallback nos custom_attributes mf_* do Chatwoot.
 */
export function extractDecisionFromTurn(turn, customAttributes = null) {
    const steps = Array.isArray(turn?.steps) ? turn.steps : [];
    const decisionSteps = steps.filter(
        (s) => s?.stage === "intent.resolved" || s?.stage === "handoff.triggered"
    );
    const lastStep = decisionSteps[decisionSteps.length - 1];
    // Merge chronológico: intent.resolved traz sticky/CRM; handoff.triggered sobrescreve
    // o que tiver (intent, team, handoff), sem apagar campos só do turno IA.
    const attrs = {};
    for (const step of decisionSteps) {
        if (step?.attrs && typeof step.attrs === "object") {
            Object.assign(attrs, step.attrs);
        }
    }
    const ca =
        customAttributes && typeof customAttributes === "object" ? customAttributes : {};

    const pick = (...keys) => {
        for (const key of keys) {
            if (attrs[key] != null && attrs[key] !== "") return attrs[key];
        }
        return null;
    };
    const pickMf = (mfKey, ...plainKeys) =>
        firstFilled([pick(...plainKeys), ca[mfKey]]);

    let handoff = null;
    if (lastStep?.stage === "handoff.triggered") {
        handoff = true;
    } else if (attrs.handoff != null) {
        handoff = parseTriBool(attrs.handoff);
    }

    const escalationRaw = pick("escalation") ?? ca.mf_escalation;
    const escalation = parseTriBool(escalationRaw);

    return {
        intent: pickMf("mf_intent", "intent"),
        urgency: pickMf("mf_urgency", "urgency"),
        handoff,
        escalation,
        teamId: firstFilled([
            pick("team_id", "team"),
            ca.mf_handoff_team_id,
        ]),
        clientSummary: pickMf("mf_client_summary", "client_summary", "summary") ?? ca.mf_summary,
        triageReasoning: pickMf("mf_triage_reasoning", "triage_reasoning"),
        humanReasoning: pickMf(
            "mf_human_escalation_reasoning",
            "human_escalation_reasoning"
        ),
        mgmtReasoning: pickMf(
            "mf_mgmt_escalation_reasoning",
            "mgmt_escalation_reasoning"
        ),
        motivo: firstFilled([
            pick("motivo", "reason", "handoff_reason"),
            ca.mf_handoff_motivo,
        ]),
        motivoTexto: pickMf("mf_handoff_motivo_texto", "motivo_texto"),
        intentSticky: parseTriBool(pick("intent_sticky")),
        intentStickyMotivo: firstFilled([
            pick("intent_sticky_motivo", "sticky_motivo"),
        ]),
        crmMatched: parseTriBool(pick("crm_matched")),
        crmOpenCount: (() => {
            const raw = pick("crm_open_count");
            if (raw == null || raw === "") return null;
            const n = Number(raw);
            return Number.isFinite(n) ? n : null;
        })(),
    };
}

/** Indica se há algo útil para mostrar no card Decisão. */
export function hasDecisionData(decision) {
    if (!decision || typeof decision !== "object") return false;
    return Boolean(
        decision.intent ||
            decision.urgency ||
            decision.handoff != null ||
            decision.escalation != null ||
            decision.teamId ||
            decision.clientSummary ||
            decision.triageReasoning ||
            decision.humanReasoning ||
            decision.mgmtReasoning ||
            decision.motivo ||
            decision.motivoTexto ||
            decision.intentSticky != null ||
            decision.intentStickyMotivo ||
            decision.crmMatched != null ||
            decision.crmOpenCount != null
    );
}

/**
 * Memória de *entrada* do turno (o que o modelo recebeu), a partir de llm.request.
 * CRM pode cair de intent.resolved em trilhas legadas sem os novos attrs.
 */
export function extractMemoryFromTurn(turn) {
    const steps = Array.isArray(turn?.steps) ? turn.steps : [];
    const llmSteps = steps.filter((s) => s?.stage === "llm.request");
    const llm = llmSteps[llmSteps.length - 1];
    const llmAttrs =
        llm?.attrs && typeof llm.attrs === "object" ? llm.attrs : null;

    const empty = {
        historicoTurnos: null,
        historicoAmostra: [],
        estadoIntent: null,
        estadoResumo: null,
        estadoUrgency: null,
        crmMatched: null,
        crmOpenCount: null,
        fromLlmRequest: false,
    };
    if (!llmAttrs) return empty;

    const amostraRaw = llmAttrs.historico_amostra;
    let historicoAmostra = [];
    if (Array.isArray(amostraRaw)) {
        historicoAmostra = amostraRaw.map((x) => String(x ?? "")).filter(Boolean);
    } else if (typeof amostraRaw === "string" && amostraRaw.trim()) {
        historicoAmostra = [amostraRaw.trim()];
    }

    const turnosRaw = llmAttrs.historico_turnos;
    let historicoTurnos = null;
    if (turnosRaw != null && turnosRaw !== "") {
        const n = Number(turnosRaw);
        historicoTurnos = Number.isFinite(n) ? n : null;
    }

    const firstFilledLocal = (values) => {
        for (const v of values) {
            if (v != null && String(v).trim() !== "") return String(v);
        }
        return null;
    };

    let crmMatched = parseTriBool(llmAttrs.crm_matched);
    let crmOpenCount = null;
    if (llmAttrs.crm_open_count != null && llmAttrs.crm_open_count !== "") {
        const n = Number(llmAttrs.crm_open_count);
        if (Number.isFinite(n)) crmOpenCount = n;
    }

    // Legado: llm.request só tinha janela — CRM vinha no intent.resolved.
    if (crmMatched == null && crmOpenCount == null) {
        const intentSteps = steps.filter((s) => s?.stage === "intent.resolved");
        const lastIntent = intentSteps[intentSteps.length - 1];
        const ia =
            lastIntent?.attrs && typeof lastIntent.attrs === "object"
                ? lastIntent.attrs
                : {};
        if (crmMatched == null) crmMatched = parseTriBool(ia.crm_matched);
        if (crmOpenCount == null && ia.crm_open_count != null && ia.crm_open_count !== "") {
            const n = Number(ia.crm_open_count);
            if (Number.isFinite(n)) crmOpenCount = n;
        }
    }

    return {
        historicoTurnos,
        historicoAmostra,
        estadoIntent: firstFilledLocal([llmAttrs.estado_intent]),
        estadoResumo: firstFilledLocal([llmAttrs.estado_resumo]),
        estadoUrgency: firstFilledLocal([llmAttrs.estado_urgency]),
        crmMatched,
        crmOpenCount,
        fromLlmRequest: true,
    };
}

export function hasMemoryData(memory) {
    if (!memory || typeof memory !== "object") return false;
    if (!memory.fromLlmRequest) return false;
    return Boolean(
        memory.historicoTurnos != null ||
            (Array.isArray(memory.historicoAmostra) &&
                memory.historicoAmostra.length > 0) ||
            memory.estadoIntent ||
            memory.estadoResumo ||
            memory.estadoUrgency ||
            memory.crmMatched != null ||
            memory.crmOpenCount != null
    );
}

const TONE_CLASSES = {
    ok: "border-emerald-500/50 bg-emerald-950/35 text-emerald-100",
    erro: "border-red-500/60 bg-red-950/45 text-red-100",
    ignorado: "border-amber-500/60 bg-amber-950/40 text-amber-100",
    humano: "border-sky-400/50 bg-sky-950/35 text-sky-100",
    substituido: "border-gray-500/50 bg-gray-800/50 text-gray-200",
    andamento: "border-[#d59106]/70 bg-[#d59106]/12 text-amber-100",
    parado: "border-orange-500/60 bg-orange-950/40 text-orange-100",
    vazio: "border-dashed border-gray-600 bg-gray-900/40 text-gray-300",
};

const TONE_BAR = {
    ok: "bg-emerald-400",
    erro: "bg-red-400",
    ignorado: "bg-amber-400",
    humano: "bg-sky-400",
    substituido: "bg-gray-400",
    andamento: "bg-[#d59106]",
    parado: "bg-orange-400",
    vazio: "bg-gray-600",
};

const STATUS_PILL = {
    open: { label: "aberta", className: "bg-emerald-900/60 text-emerald-200 border-emerald-600/50" },
    pending: { label: "pendente", className: "bg-amber-900/60 text-amber-200 border-amber-600/50" },
    resolved: { label: "resolvida", className: "bg-gray-700/70 text-gray-300 border-gray-500/50" },
    snoozed: { label: "adiada", className: "bg-violet-900/50 text-violet-200 border-violet-600/40" },
};

const statusDotClass = (status) => {
    if (status === "erro") return "bg-red-400";
    if (status === "ignorado") return "bg-amber-400";
    return "bg-emerald-400";
};

/**
 * Resumo da rajada a partir dos steps do turno (appended + flushed).
 * Usado nas informações úteis: quantas msgs e tempo entre elas.
 */
export function summarizeDebounceRajada(steps) {
    const list = Array.isArray(steps) ? steps : [];
    const appended = list.filter((s) => s?.stage === "debounce.appended");
    const flushed = list.find((s) => s?.stage === "debounce.flushed");
    const times = appended
        .map((s) => tsValue(s.ts))
        .filter((n) => Number.isFinite(n))
        .sort((a, b) => a - b);
    const intervalosMs = [];
    for (let i = 1; i < times.length; i++) {
        intervalosMs.push(Math.max(0, times[i] - times[i - 1]));
    }
    const partesAttr = flushed?.attrs?.partes ?? appended.at(-1)?.attrs?.parte;
    const mensagens = Number(partesAttr) || appended.length || 0;
    const quietRaw = appended.map((s) => s?.attrs?.quiet_ms).find((v) => v != null);
    return {
        mensagens,
        intervalosMs,
        intervaloMedioMs: intervalosMs.length
            ? Math.round(intervalosMs.reduce((a, b) => a + b, 0) / intervalosMs.length)
            : null,
        janelaMs: times.length >= 2 ? times[times.length - 1] - times[0] : null,
        quietMs: quietRaw != null && Number.isFinite(Number(quietRaw)) ? Number(quietRaw) : null,
    };
}

/** Mescla resumo da rajada nos attrs do step (flushed/appended) para usefulStepAttrs. */
export function attrsWithDebounceRajada(step, steps) {
    const base =
        step?.attrs && typeof step.attrs === "object" ? { ...step.attrs } : {};
    if (!step || !["debounce.flushed", "debounce.appended"].includes(step.stage)) {
        return base;
    }
    const summary = summarizeDebounceRajada(steps);
    if (summary.mensagens > 0) base.mensagens = summary.mensagens;
    if (summary.intervalosMs.length === 1) {
        base.intervalo_entre_msgs = formatDuration(summary.intervalosMs[0]);
    } else if (summary.intervalosMs.length > 1) {
        base.intervalo_entre_msgs = summary.intervalosMs
            .map((ms) => formatDuration(ms))
            .join(" \u00b7 ");
    } else if (summary.intervaloMedioMs != null) {
        base.intervalo_entre_msgs = formatDuration(summary.intervaloMedioMs);
    }
    if (summary.janelaMs != null && step.stage === "debounce.flushed") {
        base.janela_rajada = formatDuration(summary.janelaMs);
    }
    if (summary.quietMs != null && base.quiet_ms == null) {
        base.quiet_ms = summary.quietMs;
    }
    return base;
}

/** Atributos úteis de uma etapa para o detalhe expandido. */
export function usefulStepAttrs(attrs) {
    if (!attrs || typeof attrs !== "object") return [];
    const prefer = [
        ["mensagens", "mensagens"],
        ["historico_turnos", "hist\u00f3rico"],
        ["historico_amostra", "amostra"],
        ["estado_intent", "estado intent"],
        ["intervalo_entre_msgs", "entre msgs"],
        ["janela_rajada", "janela"],
        ["quiet_ms", "sil\u00eancio"],
        ["parte", "msg #"],
        ["partes", "mensagens"],
        ["urgency", "urg\u00eancia"],
        ["triage_reasoning", "triagem"],
        ["client_summary", "resumo cliente"],
        ["motivo_texto", "motivo (texto)"],
        ["gen_ai.request.model", "modelo"],
        ["model", "modelo"],
        ["duration_ms", "lat\u00eancia"],
        ["gen_ai.usage.input_tokens", "tokens in"],
        ["gen_ai.usage.output_tokens", "tokens out"],
        ["motivo", "motivo"],
        ["reason", "motivo"],
        ["owner", "dono"],
        ["assignee", "designado"],
        ["team", "equipe"],
        ["handoff_reason", "handoff"],
        ["message_id", "msg"],
        ["chunks", "chunks"],
    ];
    const rows = [];
    const used = new Set();
    for (const [key, label] of prefer) {
        if (attrs[key] == null || attrs[key] === "") continue;
        // Evita duplicar "mensagens" se já veio de `mensagens` e `partes`.
        if (key === "partes" && used.has("mensagens")) continue;
        used.add(key === "partes" ? "mensagens" : key);
        let value = attrs[key];
        if (key === "duration_ms" || key === "quiet_ms") {
            value = formatDuration(Number(value));
        } else if (Array.isArray(value)) value = value.slice(0, 3).join(", ");
        else value = String(value);
        rows.push({ label, value });
    }
    for (const [key, value] of Object.entries(attrs)) {
        if (used.has(key)) continue;
        if (key === "partes" && used.has("mensagens")) continue;
        if (value == null || value === "") continue;
        if (/prompt|content|secret|token|password|key/i.test(key)) continue;
        if (typeof value === "object") continue;
        rows.push({ label: key, value: String(value) });
        if (rows.length >= 8) break;
    }
    return rows;
}

const MetaChip = ({ label, value, tone = "neutral", testId }) => {
    const tones = {
        neutral: "border-gray-600/70 bg-gray-800/70 text-gray-200",
        good: "border-emerald-600/50 bg-emerald-950/40 text-emerald-100",
        warn: "border-amber-600/50 bg-amber-950/40 text-amber-100",
        bad: "border-red-600/50 bg-red-950/40 text-red-100",
        accent: "border-[#d59106]/50 bg-[#d59106]/10 text-amber-100",
        info: "border-sky-600/50 bg-sky-950/40 text-sky-100",
    };
    return (
        <div
            className={`rounded-md border px-2 py-1.5 ${tones[tone] ?? tones.neutral}`}
            data-testid={testId}
        >
            <p className="text-[9px] uppercase tracking-wide opacity-70">{label}</p>
            <p className="mt-0.5 text-xs font-medium leading-snug break-words">{value}</p>
        </div>
    );
};

const StepRow = ({ step, highlight, turnSteps }) => {
    const details = usefulStepAttrs(attrsWithDebounceRajada(step, turnSteps));
    return (
        <li
            className={`rounded-md px-2 py-1.5 transition-colors ${
                highlight
                    ? "bg-[#d59106]/15 border border-[#d59106]/60"
                    : "border border-transparent hover:bg-gray-800/50"
            }`}
            data-testid={`trace-step-${step.stage}`}
        >
            <div className="flex items-start gap-2">
                <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${statusDotClass(step.status)}`}
                    aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                    <p
                        className={`text-xs ${
                            highlight ? "font-semibold text-amber-100" : "text-gray-200"
                        }`}
                    >
                        {stageLabel(step.stage)}
                    </p>
                    <p className="text-[11px] text-gray-400">
                        {formatClock(step.ts)}
                        {step.deltaMs != null ? ` \u00b7 +${formatDuration(step.deltaMs)}` : ""}
                    </p>
                    {details.length > 0 ? (
                        <dl className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5">
                            {details.slice(0, 4).map((d) => (
                                <div key={`${d.label}-${d.value}`} className="min-w-0">
                                    <dt className="inline text-[9px] uppercase text-gray-500">
                                        {d.label}:{" "}
                                    </dt>
                                    <dd className="inline break-all font-mono text-[10px] text-gray-300">
                                        {d.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    ) : null}
                </div>
            </div>
        </li>
    );
};

const DiagnosisBox = ({ diagnosis, testId, compact = false }) => (
    <div
        className={`rounded-md border ${compact ? "p-1.5" : "p-2"} ${
            TONE_CLASSES[diagnosis.tone] ?? TONE_CLASSES.vazio
        }`}
        data-testid={testId}
    >
        <p className={`font-semibold ${compact ? "text-[11px]" : "text-xs"}`}>
            {diagnosis.headline}
        </p>
        {!compact ? (
            <>
                <p className="mt-1 text-xs leading-snug">{diagnosis.detail}</p>
                {diagnosis.extra ? (
                    <p className="mt-1 text-[11px] opacity-80">{diagnosis.extra}</p>
                ) : null}
                {diagnosis.code ? (
                    <p className="mt-1 break-all font-mono text-[10px] opacity-70">
                        {TXT.code}: {diagnosis.code}
                    </p>
                ) : null}
            </>
        ) : null}
    </div>
);

const urgencyTone = (urgency) => {
    const key = String(urgency || "")
        .trim()
        .toLowerCase();
    if (key === "high" || key === "alta" || key === "urgent") return "bad";
    if (key === "medium" || key === "media" || key === "média") return "warn";
    return "neutral";
};

const DecisionCard = ({ decision, testId = "turn-trace-decision" }) => {
    if (!hasDecisionData(decision)) return null;

    const textBlocks = [
        [TXT.decisionClientSummary, decision.clientSummary],
        [TXT.decisionTriage, decision.triageReasoning],
        [TXT.decisionHuman, decision.humanReasoning],
        [TXT.decisionMgmt, decision.mgmtReasoning],
        [TXT.decisionMotivoTexto, decision.motivoTexto],
        [TXT.decisionStickyMotivo, decision.intentStickyMotivo],
    ].filter(([, value]) => value);

    return (
        <div
            className="rounded-lg border border-[#b47c06]/40 bg-gradient-to-br from-gray-900/80 to-gray-950/60 p-2.5"
            data-testid={testId}
        >
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-[#d59106]/90">
                {TXT.decisionTitle}
            </p>
            <div className="grid grid-cols-2 gap-1.5">
                {decision.intent ? (
                    <MetaChip
                        label={TXT.decisionIntent}
                        value={String(decision.intent)}
                        tone="accent"
                        testId="turn-trace-decision-intent"
                    />
                ) : null}
                {decision.urgency ? (
                    <MetaChip
                        label={TXT.decisionUrgency}
                        value={String(decision.urgency)}
                        tone={urgencyTone(decision.urgency)}
                        testId="turn-trace-decision-urgency"
                    />
                ) : null}
                {decision.handoff != null ? (
                    <MetaChip
                        label={TXT.decisionHandoff}
                        value={formatTriBool(decision.handoff) ?? String(decision.handoff)}
                        tone={decision.handoff ? "warn" : "good"}
                        testId="turn-trace-decision-handoff"
                    />
                ) : null}
                {decision.escalation != null ? (
                    <MetaChip
                        label={TXT.decisionEscalation}
                        value={formatTriBool(decision.escalation) ?? String(decision.escalation)}
                        tone={decision.escalation ? "warn" : "neutral"}
                        testId="turn-trace-decision-escalation"
                    />
                ) : null}
                {decision.intentSticky != null ? (
                    <MetaChip
                        label={TXT.decisionSticky}
                        value={formatTriBool(decision.intentSticky) ?? String(decision.intentSticky)}
                        tone={decision.intentSticky ? "info" : "neutral"}
                        testId="turn-trace-decision-sticky"
                    />
                ) : null}
                {decision.crmMatched != null ? (
                    <MetaChip
                        label={TXT.decisionCrm}
                        value={formatTriBool(decision.crmMatched) ?? String(decision.crmMatched)}
                        tone={decision.crmMatched ? "info" : "neutral"}
                        testId="turn-trace-decision-crm"
                    />
                ) : null}
                {decision.crmOpenCount != null ? (
                    <MetaChip
                        label={TXT.decisionCrmOpen}
                        value={String(decision.crmOpenCount)}
                        tone={decision.crmOpenCount > 0 ? "warn" : "neutral"}
                        testId="turn-trace-decision-crm-open"
                    />
                ) : null}
                {decision.teamId ? (
                    <MetaChip
                        label={TXT.decisionTeam}
                        value={String(decision.teamId)}
                        tone="info"
                        testId="turn-trace-decision-team"
                    />
                ) : null}
                {decision.motivo ? (
                    <MetaChip
                        label={TXT.decisionMotivo}
                        value={String(decision.motivo)}
                        tone="warn"
                        testId="turn-trace-decision-motivo"
                    />
                ) : null}
            </div>
            {textBlocks.length > 0 ? (
                <dl className="mt-2 space-y-1">
                    {textBlocks.map(([label, value]) => (
                        <div key={label} className="min-w-0">
                            <dt className="text-[9px] uppercase tracking-wide text-gray-500">
                                {label}
                            </dt>
                            <dd className="mt-0.5 text-xs leading-snug text-gray-200 break-words">
                                {value}
                            </dd>
                        </div>
                    ))}
                </dl>
            ) : null}
        </div>
    );
};

const MemoryCard = ({ memory, testId = "turn-trace-memory" }) => {
    if (!hasMemoryData(memory)) return null;

    const hasEstado = Boolean(
        memory.estadoIntent || memory.estadoResumo || memory.estadoUrgency
    );
    const hasCrm = memory.crmMatched != null || memory.crmOpenCount != null;

    return (
        <div
            className="rounded-lg border border-sky-700/40 bg-gradient-to-br from-gray-900/80 to-slate-950/60 p-2.5"
            data-testid={testId}
        >
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-sky-300/90">
                {TXT.memoryTitle}
            </p>

            <div className="grid grid-cols-2 gap-1.5">
                {memory.historicoTurnos != null ? (
                    <MetaChip
                        label={TXT.memoryJanela}
                        value={String(memory.historicoTurnos)}
                        tone="accent"
                        testId="turn-trace-memory-janela"
                    />
                ) : null}
                {memory.estadoIntent ? (
                    <MetaChip
                        label={TXT.memoryEstadoIntent}
                        value={String(memory.estadoIntent)}
                        tone="info"
                        testId="turn-trace-memory-estado-intent"
                    />
                ) : null}
                {memory.estadoUrgency ? (
                    <MetaChip
                        label={TXT.memoryEstadoUrgency}
                        value={String(memory.estadoUrgency)}
                        tone={urgencyTone(memory.estadoUrgency)}
                        testId="turn-trace-memory-estado-urgency"
                    />
                ) : null}
                {memory.crmMatched != null ? (
                    <MetaChip
                        label={TXT.memoryCrm}
                        value={formatTriBool(memory.crmMatched) ?? String(memory.crmMatched)}
                        tone={memory.crmMatched ? "info" : "neutral"}
                        testId="turn-trace-memory-crm"
                    />
                ) : null}
                {memory.crmOpenCount != null ? (
                    <MetaChip
                        label={TXT.memoryCrmOpen}
                        value={String(memory.crmOpenCount)}
                        tone={memory.crmOpenCount > 0 ? "warn" : "neutral"}
                        testId="turn-trace-memory-crm-open"
                    />
                ) : null}
            </div>

            <div className="mt-2 space-y-1.5">
                <div data-testid="turn-trace-memory-amostra">
                    <p className="text-[9px] uppercase tracking-wide text-gray-500">
                        {TXT.memoryAmostra}
                    </p>
                    {memory.historicoAmostra.length > 0 ? (
                        <ul className="mt-0.5 space-y-0.5">
                            {memory.historicoAmostra.map((line, i) => (
                                <li
                                    key={`${i}-${line.slice(0, 24)}`}
                                    className="font-mono text-[10px] leading-snug text-gray-300 break-words"
                                >
                                    {line}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-0.5 text-[11px] text-gray-500">
                            {TXT.memoryAmostraVazia}
                        </p>
                    )}
                </div>

                <div>
                    <p className="text-[9px] uppercase tracking-wide text-gray-500">
                        {TXT.memoryEstado}
                    </p>
                    {hasEstado ? (
                        memory.estadoResumo ? (
                            <p
                                className="mt-0.5 text-xs leading-snug text-gray-200 break-words"
                                data-testid="turn-trace-memory-estado-resumo"
                            >
                                {memory.estadoResumo}
                            </p>
                        ) : (
                            <p className="mt-0.5 text-[11px] text-gray-500">
                                {TXT.memoryEstadoVazio}
                            </p>
                        )
                    ) : (
                        <p className="mt-0.5 text-[11px] text-gray-500">
                            {TXT.memoryEstadoVazio}
                        </p>
                    )}
                </div>

                {!hasCrm ? (
                    <p className="text-[11px] text-gray-500">{TXT.memoryCrmVazio}</p>
                ) : null}
            </div>
        </div>
    );
};

/**
 * Mescla estado da trilha com live Chatwoot (SoR da sessão).
 * Live manda em status/dono/assignee — a trilha atrasa e causava tags divergentes.
 */
export function mergeConversationLive(state, conversationLive) {
    if (!conversationLive || typeof conversationLive !== "object") return state;
    const base = state && typeof state === "object" ? state : {};
    const conv = { ...(base.conversation || {}) };
    const owner = { ...(base.owner || {}) };
    if (conversationLive.status != null && conversationLive.status !== "") {
        conv.status = conversationLive.status;
    }
    if (conversationLive.inbox != null && conversationLive.inbox !== "") {
        conv.inbox = conversationLive.inbox;
    }
    if (conversationLive.handoff_reason != null) {
        conv.handoff_reason = conversationLive.handoff_reason;
    }
    if (conversationLive.owner_mode != null && conversationLive.owner_mode !== "") {
        owner.mode = conversationLive.owner_mode;
    }
    if (conversationLive.assignee != null) {
        owner.assignee = conversationLive.assignee;
    }
    if (conversationLive.team != null) {
        owner.team = conversationLive.team;
    }
    if (conversationLive.assigned != null) {
        owner.assigned = conversationLive.assigned;
    }
    return { ...base, conversation: conv, owner };
}

const ChatwootStateCard = ({ state, owner }) => {
    const statusKey = String(state?.conversation?.status || "")
        .trim()
        .toLowerCase();
    const statusMeta = STATUS_PILL[statusKey] || {
        label: statusKey || "\u2014",
        className: "bg-gray-800/70 text-gray-300 border-gray-600/50",
    };
    const assignee = state?.owner?.assignee || owner?.name || null;
    const assigned =
        state?.owner?.assigned != null
            ? Boolean(state.owner.assigned)
            : Boolean(assignee);
    const team = state?.owner?.team || null;
    const inbox = state?.conversation?.inbox || null;
    const handoff = state?.conversation?.handoff_reason || null;
    const botEnabled = state?.bot?.global_enabled;
    const configVersion = state?.bot?.config_version
        ? String(state.bot.config_version).slice(0, 8)
        : null;

    return (
        <div
            className="rounded-lg border border-[#b47c06]/40 bg-gradient-to-br from-gray-900/80 to-gray-950/60 p-2.5"
            data-testid="turn-trace-cw-state"
        >
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-[#d59106]/90">
                {TXT.chatwootNow}
            </p>
            <div className="grid grid-cols-2 gap-1.5">
                <MetaChip
                    label={TXT.status}
                    value={statusMeta.label}
                    tone={
                        statusKey === "open"
                            ? "good"
                            : statusKey === "pending"
                              ? "warn"
                              : statusKey === "resolved"
                                ? "neutral"
                                : "neutral"
                    }
                    testId="turn-trace-cw-status"
                />
                <MetaChip
                    label={TXT.owner}
                    value={
                        owner?.name ? `${owner.label} (${owner.name})` : owner?.label || TXT.ownerUnknown
                    }
                    tone={
                        owner?.kind === "humano"
                            ? "info"
                            : owner?.kind === "bot"
                              ? "accent"
                              : "neutral"
                    }
                    testId="turn-trace-owner"
                />
                <MetaChip
                    label={TXT.assigned}
                    value={assigned ? `${TXT.assignedYes}${assignee ? `: ${assignee}` : ""}` : TXT.assignedNo}
                    tone={assigned ? "info" : "warn"}
                    testId="turn-trace-cw-assignee"
                />
                <MetaChip
                    label={TXT.team}
                    value={team || "\u2014"}
                    tone={team ? "neutral" : "neutral"}
                    testId="turn-trace-cw-team"
                />
                {inbox ? (
                    <MetaChip label={TXT.inbox} value={inbox} testId="turn-trace-cw-inbox" />
                ) : null}
                {botEnabled != null ? (
                    <MetaChip
                        label={TXT.botGlobal}
                        value={botEnabled ? TXT.botOn : TXT.botOff}
                        tone={botEnabled ? "good" : "bad"}
                        testId="turn-trace-cw-bot"
                    />
                ) : null}
                {configVersion ? (
                    <MetaChip
                        label={TXT.configVersion}
                        value={configVersion}
                        testId="turn-trace-cw-config"
                    />
                ) : null}
                {handoff ? (
                    <MetaChip
                        label={TXT.handoffReason}
                        value={handoff}
                        tone="warn"
                        testId="turn-trace-cw-handoff"
                    />
                ) : null}
            </div>
        </div>
    );
};

const TurnCard = ({ turn, diagnosis, platforms, isLatest, open, onToggle, customAttributes }) => {
    // Etapas: mais recente no topo (ordem inversa da cronológica interna).
    const stepsNewestFirst = [...(turn.steps || [])].reverse();
    const decision = extractDecisionFromTurn(turn, customAttributes);
    const memory = extractMemoryFromTurn(turn);

    return (
        <article
            className={`overflow-hidden rounded-lg border transition-shadow ${
                open
                    ? "border-[#d59106]/70 shadow-[0_0_0_1px_rgba(213,145,6,0.15)]"
                    : "border-gray-700/80 hover:border-[#b47c06]/50"
            } bg-gray-900/50`}
            data-testid={isLatest ? "turn-trace-current" : `turn-trace-card-${turn.turnId}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-stretch text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d59106]/60"
                aria-expanded={open}
                data-testid={`turn-trace-toggle-${turn.turnId}`}
            >
                <span
                    className={`w-1 shrink-0 ${TONE_BAR[diagnosis.tone] ?? TONE_BAR.vazio}`}
                    aria-hidden="true"
                />
                <div className="min-w-0 flex-1 space-y-1.5 p-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-mono text-[10px] text-gray-400">
                                {formatClock(turn.endedAt || turn.startedAt)}
                            </span>
                            {isLatest ? (
                                <span className="rounded bg-[#d59106]/20 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-amber-200">
                                    {TXT.latestBadge}
                                </span>
                            ) : null}
                        </div>
                        <span className="text-[10px] text-gray-500">
                            {turn.steps.length} {TXT.stepCount}
                            {turn.totalMs != null
                                ? ` \u00b7 ${formatDuration(turn.totalMs)}`
                                : ""}
                            <span className="ml-1.5 text-[#d59106]/90">
                                {open ? "\u25b2" : "\u25bc"}
                            </span>
                        </span>
                    </div>
                    <DiagnosisBox
                        diagnosis={diagnosis}
                        testId={
                            isLatest
                                ? "turn-trace-diagnosis"
                                : `turn-trace-diagnosis-${turn.turnId}`
                        }
                        compact={!open}
                    />
                    {!open ? (
                        <p className="text-[10px] text-gray-500">{TXT.expandHint}</p>
                    ) : null}
                </div>
            </button>

            {open ? (
                <div className="space-y-2 border-t border-gray-800 px-2.5 pb-2.5 pt-2">
                    <DecisionCard
                        decision={decision}
                        testId={
                            isLatest
                                ? "turn-trace-decision"
                                : `turn-trace-decision-${turn.turnId}`
                        }
                    />
                    <MemoryCard
                        memory={memory}
                        testId={
                            isLatest
                                ? "turn-trace-memory"
                                : `turn-trace-memory-${turn.turnId}`
                        }
                    />

                    {platforms.length > 0 ? (
                        <div data-testid={isLatest ? "turn-trace-platforms" : undefined}>
                            <p className="mb-1 text-[10px] uppercase tracking-wide text-gray-500">
                                {TXT.platformsTitle}
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {platforms.map((p) => (
                                    <span
                                        key={p.id}
                                        className="rounded-md bg-gray-800/90 px-1.5 py-0.5 text-[10px] text-gray-200 ring-1 ring-gray-700"
                                    >
                                        {p.label}: {p.lastShort} ({p.count})
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {turn.executionId ? (
                        <p className="font-mono text-[10px] text-gray-500">
                            {TXT.execution}: {turn.executionId}
                        </p>
                    ) : null}

                    {(() => {
                        const rajada = summarizeDebounceRajada(turn.steps);
                        if (!rajada.mensagens || rajada.mensagens < 2) return null;
                        const intervalo =
                            rajada.intervalosMs.length > 0
                                ? rajada.intervalosMs.map((ms) => formatDuration(ms)).join(" \u00b7 ")
                                : rajada.intervaloMedioMs != null
                                  ? formatDuration(rajada.intervaloMedioMs)
                                  : null;
                        return (
                            <div
                                className="grid grid-cols-2 gap-1.5 sm:grid-cols-3"
                                data-testid="turn-trace-rajada"
                            >
                                <MetaChip
                                    label="msgs na rajada"
                                    value={String(rajada.mensagens)}
                                    tone="accent"
                                    testId="turn-trace-rajada-msgs"
                                />
                                {intervalo ? (
                                    <MetaChip
                                        label="entre msgs"
                                        value={intervalo}
                                        tone="info"
                                        testId="turn-trace-rajada-intervalo"
                                    />
                                ) : null}
                                {rajada.quietMs != null ? (
                                    <MetaChip
                                        label="sil\u00eancio"
                                        value={formatDuration(rajada.quietMs)}
                                        tone="neutral"
                                        testId="turn-trace-rajada-quiet"
                                    />
                                ) : null}
                            </div>
                        );
                    })()}

                    <ol className="space-y-0.5">
                        {stepsNewestFirst.map((step, index) => (
                            <StepRow
                                key={`${step.stage}-${step.ts}-${index}`}
                                step={step}
                                turnSteps={turn.steps}
                                highlight={index === 0}
                            />
                        ))}
                    </ol>

                    <button
                        type="button"
                        onClick={onToggle}
                        className="text-[10px] text-gray-400 underline hover:text-gray-200"
                    >
                        {TXT.hideSteps}
                    </button>
                </div>
            ) : null}
        </article>
    );
};

const BotOpsTurnTrace = ({
    token,
    conversationId,
    isActive,
    onToastBad,
    ownerFallback = null,
    conversationLive = null,
    mode = "live",
    events: eventsProp = null,
    untilTs = null,
}) => {
    const [events, setEvents] = useState([]);
    const [traceState, setTraceState] = useState(null);
    const [status, setStatus] = useState("carregando");
    const [errorMessage, setErrorMessage] = useState(null);
    const [lastCheckedAt, setLastCheckedAt] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [expandedTurns, setExpandedTurns] = useState({});

    const isReplay = mode === "replay";

    const cursorRef = useRef(null);
    const seenRef = useRef(new Set());
    const errorToastedRef = useRef(false);
    // Callbacks de toast são instáveis entre renders: guardar em ref evita
    // que virem dependência de useEffect e reiniciem o polling (laço de recarga).
    const toastBadRef = useRef(onToastBad);

    useEffect(() => {
        toastBadRef.current = onToastBad;
    }, [onToastBad]);

    useEffect(() => {
        if (isReplay) {
            cursorRef.current = null;
            seenRef.current = new Set();
            setEvents(Array.isArray(eventsProp) ? eventsProp : []);
            setTraceState(null);
            setExpandedTurns({});
            setErrorMessage(null);
            setStatus("ok");
            return;
        }
        cursorRef.current = null;
        seenRef.current = new Set();
        errorToastedRef.current = false;
        setEvents([]);
        setTraceState(null);
        setExpandedTurns({});
        setErrorMessage(null);
        setStatus(conversationId ? "carregando" : "inativo");
    }, [isReplay, conversationId, eventsProp]);

    const absorb = useCallback((incoming) => {
        if (!Array.isArray(incoming) || incoming.length === 0) return;
        const fresh = [];
        for (const event of incoming) {
            if (!event || typeof event !== "object") continue;
            const key = [
                event.turn_id ?? "",
                event.stage ?? "",
                event.ts ?? "",
                event.execution_id ?? "",
            ].join("|");
            if (seenRef.current.has(key)) continue;
            seenRef.current.add(key);
            fresh.push(event);
            if (tsValue(event.ts) > tsValue(cursorRef.current)) {
                cursorRef.current = event.ts;
            }
        }
        if (fresh.length === 0) return;
        setEvents((prev) => {
            const merged = prev.concat(fresh);
            return merged.length > MAX_EVENTS ? merged.slice(merged.length - MAX_EVENTS) : merged;
        });
    }, []);

    useEffect(() => {
        if (isReplay || !isActive || !conversationId) return undefined;

        let cancelled = false;
        let timer = null;

        const schedule = (ms) => {
            if (cancelled) return;
            timer = setTimeout(run, ms);
        };

        const run = async () => {
            const data = await getSandboxTrace(token, conversationId, cursorRef.current);
            if (cancelled) return;
            setLastCheckedAt(new Date().toISOString());

            if (data?.type === "unavailable") {
                setStatus("indisponivel");
                setErrorMessage(null);
                // Endpoint pode subir a qualquer momento: segue tentando devagar.
                schedule(POLL_UNAVAILABLE_MS);
                return;
            }

            if (data?.type === "error") {
                setStatus("erro");
                setErrorMessage(data.message || TXT.errorTitle);
                if (!errorToastedRef.current) {
                    errorToastedRef.current = true;
                    toastBadRef.current?.(TXT.toastError);
                }
                // Backoff longo — nunca reagenda imediato, senão vira laço de recarga.
                schedule(POLL_BACKOFF_MS);
                return;
            }

            errorToastedRef.current = false;
            setStatus("ok");
            setErrorMessage(null);
            if (data?.state) setTraceState(data.state);
            absorb(data?.events);
            schedule(POLL_INTERVAL_MS);
        };

        run();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [isReplay, isActive, conversationId, token, refreshKey, absorb]);

    const sourceEvents = isReplay
        ? Array.isArray(eventsProp)
            ? eventsProp
            : events
        : events;
    const visibleEvents = isReplay
        ? filterEventsUntil(sourceEvents, untilTs)
        : sourceEvents;
    const nowMs = tsValue(lastCheckedAt) || Date.now();
    const turns = useMemo(
        () => groupByTurn(visibleEvents).slice(0, MAX_TURNS),
        [visibleEvents]
    );
    const mergedTraceState = useMemo(
        () => mergeConversationLive(traceState, conversationLive),
        [traceState, conversationLive]
    );
    // SoR Chatwoot live (sessão) > state da trilha > fallback.
    const ownerModeLive =
        conversationLive?.owner_mode ||
        mergedTraceState?.owner?.mode ||
        ownerFallback;
    const owner = useMemo(
        () => describeOwner(visibleEvents, ownerModeLive),
        [visibleEvents, ownerModeLive]
    );
    const customAttributes =
        mergedTraceState?.conversation?.custom_attributes ?? null;

    const isTurnOpen = (turnId, isLatest) => {
        if (Object.prototype.hasOwnProperty.call(expandedTurns, turnId)) {
            return Boolean(expandedTurns[turnId]);
        }
        return isLatest;
    };

    const toggleTurn = (turnId, isLatest) =>
        setExpandedTurns((prev) => {
            const currentlyOpen = Object.prototype.hasOwnProperty.call(prev, turnId)
                ? Boolean(prev[turnId])
                : isLatest;
            return { ...prev, [turnId]: !currentlyOpen };
        });

    return (
        <aside
            className="space-y-3 rounded-xl border border-[#b47c06]/60 bg-gradient-to-b from-gray-900/70 to-gray-950/80 p-3 shadow-lg shadow-black/20"
            aria-label={TXT.title}
            data-testid="turn-trace-panel"
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h3 className="text-sm font-semibold tracking-tight text-laranjinha">
                        {TXT.title}
                    </h3>
                    <p className="text-[11px] text-gray-400">{TXT.subtitle}</p>
                </div>
                {!isReplay ? (
                    <Button
                        click={() => setRefreshKey((k) => k + 1)}
                        disabled={!conversationId}
                        classes="bg-gray-700 hover:bg-gray-600 px-2.5 py-1 text-xs rounded-md font-medium text-white"
                    >
                        {TXT.refresh}
                    </Button>
                ) : null}
            </div>

            {status === "indisponivel" ? (
                <div
                    className="rounded-lg border border-gray-500/60 bg-gray-800/60 p-2 text-xs text-gray-300"
                    data-testid="turn-trace-unavailable"
                >
                    <p className="font-semibold">{TXT.unavailableTitle}</p>
                    <p className="mt-1 leading-snug">{TXT.unavailableBody}</p>
                </div>
            ) : null}

            {status === "erro" ? (
                <div
                    className="rounded-lg border border-red-500/60 bg-red-950/40 p-2 text-xs text-red-200"
                    role="alert"
                    data-testid="turn-trace-error"
                >
                    <p className="font-semibold">{TXT.errorTitle}</p>
                    {errorMessage ? (
                        <p className="mt-1 break-words leading-snug">{errorMessage}</p>
                    ) : null}
                    <p className="mt-1 leading-snug opacity-80">{TXT.errorBody}</p>
                </div>
            ) : null}

            {conversationId && (mergedTraceState || conversationLive || owner) ? (
                <ChatwootStateCard state={mergedTraceState} owner={owner} />
            ) : null}

            {!conversationId && !(isReplay && visibleEvents.length > 0) ? (
                <div
                    className="rounded-lg border border-dashed border-gray-600 p-3 text-xs text-gray-300"
                    data-testid="turn-trace-inactive"
                >
                    <p className="font-semibold">{TXT.noConversationTitle}</p>
                    <p className="mt-1 leading-snug">{TXT.noConversationBody}</p>
                </div>
            ) : null}

            {/* Sem leitura da trilha não afirmamos "nada aconteceu" — só que não sabemos. */}
            {conversationId && turns.length === 0 && status !== "indisponivel" && status !== "erro" ? (
                <div
                    className="rounded-lg border border-dashed border-gray-600 p-3 text-xs text-gray-300"
                    data-testid="turn-trace-empty"
                >
                    <p className="font-semibold">
                        {status === "carregando" ? TXT.checking : TXT.emptyTitle}
                    </p>
                    <p className="mt-1 leading-snug">
                        {status === "carregando" ? TXT.checkingBody : TXT.emptyBody}
                    </p>
                </div>
            ) : null}

            {turns.length > 0 ? (
                <div className="space-y-2" data-testid="turn-trace-list">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        {TXT.turnsTitle}
                    </p>
                    {turns.map((turn, index) => {
                        const isLatest = index === 0;
                        const diagnosis = buildTurnDiagnosis(turn, nowMs);
                        const platforms = summarizePlatforms(turn);
                        const open = isTurnOpen(turn.turnId, isLatest);
                        return (
                            <TurnCard
                                key={turn.turnId}
                                turn={turn}
                                diagnosis={diagnosis}
                                platforms={platforms}
                                isLatest={isLatest}
                                open={open}
                                onToggle={() => toggleTurn(turn.turnId, isLatest)}
                                customAttributes={customAttributes}
                            />
                        );
                    })}
                </div>
            ) : null}

            {lastCheckedAt ? (
                <p className="text-[10px] text-gray-500">
                    {TXT.lastCheck}: {formatClock(lastCheckedAt)}
                </p>
            ) : null}
        </aside>
    );
};

export default BotOpsTurnTrace;
